import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useProfile } from "../hooks/useProfile";
import PrivacyToggle from "../components/PrivacyToggle";
import ComboBox from "../components/ComboBox";
import { UNIVERSITIES } from "../constants/universities";
import { INDUSTRIES } from "../constants/industries";
import { US_STATES } from "../constants/usStates";
import Nav from "../components/Nav";
import { initialsFor } from "../lib/initials";


export default function Profile() {
  const { user } = useAuth();
  const { profile, loading, updateProfile } = useProfile(user?.uid);

  const [fullName, setFullName] = useState("");
  const [school, setSchool] = useState("");
  const [industry, setIndustry] = useState("");
  const [role, setRole] = useState("");
  const [organization, setOrganization] = useState("");
  const [location, setLocation] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [privacy, setPrivacy] = useState({
    show_email: false,
    show_school: true,
    show_industry: true,
    show_role: true,
    show_organization: true,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name || "");
    setSchool(profile.school || "");
    setIndustry(profile.industry || "");
    setRole(profile.role || "");
    setOrganization(profile.organization || "");
    setLocation(profile.location || "");
    setCity(profile.city || "");
    setState(profile.state || "");
    setPrivacy({
      show_email: false,
      show_school: true,
      show_industry: true,
      show_role: true,
      show_organization: true,
      ...(profile.privacy_settings || {}),
    });
  }, [profile]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await updateProfile({
        ...(!profile ? { uid: user.uid, email: user.email, phone_number: "" } : {}),
        full_name: fullName.trim(),
        school: school.trim(),
        industry: industry.trim(),
        role: role.trim(),
        organization: organization.trim(),
        location: location.trim(),
        city: city.trim(),
        state: state.trim(),
        privacy_settings: privacy,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      alert("Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Nav variant="minimal" />
        <div className="page-wrapper">
          <div className="loading-center"><div className="spinner" /></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Nav variant="minimal" />
      <div className="page-wrapper">
        <div className="profile-page">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <div>
              <p className="eyebrow">Settings</p>
              <h2 style={{ fontSize: "var(--font-lg)", fontWeight: 700 }}>Your Profile</h2>
            </div>
            <Link to="/dashboard" className="back-link">← Dashboard</Link>
          </div>

          {/* Avatar (initials only) */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
            <div className="profile-photo-circle">
              {initialsFor(fullName)}
            </div>
          </div>

          {/* Personal Info */}
          <div className="profile-section">
            <div className="profile-section-header">
              <p className="eyebrow">Personal info</p>
            </div>
            <div className="profile-section-body">
              <div className="form-field">
                <label>Full name</label>
                <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div className="form-field">
                <label>School</label>
                <ComboBox value={school} onChange={setSchool} options={UNIVERSITIES} placeholder="e.g. Cornell University" />
              </div>
              <div className="form-field">
                <label>Industry</label>
                <ComboBox value={industry} onChange={setIndustry} options={INDUSTRIES} placeholder="e.g. Finance, Tech" />
              </div>
              <div className="form-field">
                <label>Role</label>
                <input type="text" value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Junior, Finance Major" />
              </div>
              <div className="form-field">
                <label>Organization or fraternity</label>
                <input
                  type="text"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="e.g. Alpha Phi Alpha"
                />
              </div>
              <div className="city-state-row">
                <div className="form-field">
                  <label>City</label>
                  <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
                </div>
                <div className="form-field">
                  <label>State</label>
                  <ComboBox value={state} onChange={setState} options={US_STATES} placeholder="State" />
                </div>
              </div>
              <div className="form-field">
                <label>Phone number</label>
                <div className="phone-readonly">
                  <span>{profile?.phone_number || "—"}</span>
                  <span className="phone-readonly-note">Contact support to change</span>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy */}
          <div className="profile-section">
            <div className="profile-section-header">
              <p className="eyebrow">Privacy settings</p>
            </div>
            <div className="profile-section-body" style={{ gap: 0 }}>
              <PrivacyToggle
                id="show_email"
                label="Email address"
                description="Show your email to your connections"
                value={privacy.show_email}
                onChange={(v) => setPrivacy((p) => ({ ...p, show_email: v }))}
              />
              <PrivacyToggle
                id="show_school"
                label="School"
                description="Show your school to your connections"
                value={privacy.show_school}
                onChange={(v) => setPrivacy((p) => ({ ...p, show_school: v }))}
              />
              <PrivacyToggle
                id="show_industry"
                label="Industry"
                description="Show your industry to your connections"
                value={privacy.show_industry}
                onChange={(v) => setPrivacy((p) => ({ ...p, show_industry: v }))}
              />
              <PrivacyToggle
                id="show_role"
                label="Role"
                description="Show your role/year to your connections"
                value={privacy.show_role}
                onChange={(v) => setPrivacy((p) => ({ ...p, show_role: v }))}
              />
              <PrivacyToggle
                id="show_organization"
                label="Organization or fraternity"
                description="Show this affiliation to your connections"
                value={privacy.show_organization}
                onChange={(v) => setPrivacy((p) => ({ ...p, show_organization: v }))}
              />
            </div>
          </div>

          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : saved ? "Saved!" : "Save profile"}
          </button>
        </div>
      </div>
    </>
  );
}
